import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import { editorPathFor, folderPath, templateFor } from '@/shared/folders';
import type { FoldersNode } from '@/shared/folders';
import { NewBadge } from './NewBadge';
import { NodeMenu, type NodeAction } from './NodeMenu';
import {
  layoutNodeListColumns,
  NODE_LIST_DRAG_MIN,
  type NodeListColumnId,
  type NodeListColumnWidths,
} from './nodeListColumns';

export interface NodeListRow {
  node: FoldersNode;
  actions: readonly NodeAction[];
  /** Already formatted, e.g. "2 days ago" — the column header names it. */
  date: string;
  /** Omit on every row to drop the Owner column. */
  owner?: string;
  starred?: boolean;
  /** Shared with this person and not opened since: shows a New label. */
  isNew?: boolean;
  onToggleStar?: () => void;
  /** For a row that is not a Folders node (a Numtable library document): where
   *  it opens, its icon and its type label. */
  to?: string;
  icon?: IconName;
  typeLabel?: string;
}

interface Props {
  rows: readonly NodeListRow[];
  /** Header of the date column — "Last opened", "Edited". */
  dateLabel: string;
}

type ColumnWidths = NodeListColumnWidths;

const WIDTHS_KEY = 'numtable.nodeList.columnWidths';
const DEFAULT_WIDTHS: ColumnWidths = { type: 112, owner: 144, date: 160 };

function readWidths(): ColumnWidths {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(WIDTHS_KEY) ?? '{}');
    const out: ColumnWidths = { ...DEFAULT_WIDTHS };
    if (raw && typeof raw === 'object') {
      for (const id of ['name', 'type', 'owner', 'date'] as const) {
        const v = (raw as Record<string, unknown>)[id];
        if (typeof v === 'number' && v >= NODE_LIST_DRAG_MIN) out[id] = v;
      }
    }
    return out;
  } catch {
    return { ...DEFAULT_WIDTHS };
  }
}

function withWidth(widths: ColumnWidths, id: NodeListColumnId, width: number): ColumnWidths {
  const next: ColumnWidths = { ...widths };
  next[id] = Math.max(NODE_LIST_DRAG_MIN, Math.round(width));
  return next;
}

/** Column widths every list shares, kept per browser. */
function useColumnWidths() {
  const [widths, setWidths] = useState(readWidths);
  const latest = useRef(widths);

  const commit = (next: ColumnWidths) => {
    latest.current = next;
    setWidths(next);
  };
  const persist = (next: ColumnWidths) => {
    try { localStorage.setItem(WIDTHS_KEY, JSON.stringify(next)); } catch { /* per-browser nicety only */ }
  };

  const startResize = (id: NodeListColumnId) => (event: ReactPointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    // Start from the width on screen: a column may currently be narrower than
    // its stored preference because the window squeezed it.
    const startWidth =
      event.currentTarget.parentElement?.getBoundingClientRect().width ??
      latest.current[id] ??
      DEFAULT_WIDTHS.type;
    let next = latest.current;
    const move = (moveEvent: PointerEvent) => {
      next = withWidth(next, id, startWidth + moveEvent.clientX - startX);
      commit(next);
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      persist(latest.current);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };

  /** Keyboard counterpart of the drag, so the separator is not mouse-only. */
  const nudge = (id: NodeListColumnId, from: number, delta: number) => {
    const next = withWidth(latest.current, id, from + delta);
    commit(next);
    persist(next);
  };

  return { widths, startResize, nudge };
}

/**
 * Folders and files as rows: the list half of Home's list/cards switch.
 *
 * The same nodes NodeCard shows, minus the preview, plus the columns a preview
 * hides. Below `sm` the metadata columns collapse into one line under the
 * name, because five columns at 375px is a horizontal scrollbar.
 *
 * Above `sm` the widths are computed from the box the list actually sits in
 * (see `nodeListColumns.ts`): dragged widths are honoured, the name column
 * absorbs the slack until someone drags it, and the table only scrolls once no
 * column can give up another pixel.
 */
export function NodeList({ rows, dateLabel }: Props) {
  const { t } = useI18n();
  const showOwner = rows.some((row) => row.owner !== undefined);
  const { widths, startResize, nudge } = useColumnWidths();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;
    const measure = () => {
      setContainerWidth((current) => (current === element.clientWidth ? current : element.clientWidth));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const layout = layoutNodeListColumns(containerWidth, widths, showOwner);
  const columns = 'grid-cols-[1fr_auto] sm:grid-cols-[var(--node-list-cols)]';
  const header = (id: NodeListColumnId, label: string) => {
    const width = layout.widths.find((column) => column.id === id)?.width ?? widths[id] ?? 160;
    return (
      <span className="relative min-w-0">
        <span className="block truncate">{label}</span>
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={t('home.columns.resize', { column: label })}
          aria-valuenow={Math.round(width)}
          aria-valuemin={NODE_LIST_DRAG_MIN}
          tabIndex={0}
          onPointerDown={startResize(id)}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            nudge(id, width, event.key === 'ArrowLeft' ? -16 : 16);
          }}
          className="absolute -right-2 top-[-0.5rem] bottom-[-0.5rem] w-2 cursor-col-resize touch-none select-none hover:bg-accent active:bg-accent focus-visible:bg-accent focus-visible:outline-none"
        />
      </span>
    );
  };

  return (
    <div
      ref={wrapperRef}
      className="overflow-x-auto rounded-token border border-border bg-surface-2"
    >
      <div
        className="sm:min-w-[var(--node-list-min-width)]"
        style={{
          '--node-list-cols': layout.template,
          '--node-list-min-width': `${layout.totalWidth}px`,
        } as CSSProperties}
      >
        <div
          className={`hidden ${columns} items-center gap-3 border-b border-border px-3 py-2 text-[0.6875rem] font-medium uppercase tracking-wide text-muted sm:grid`}
        >
          {header('name', t('home.columns.name'))}
          {header('type', t('home.columns.type'))}
          {showOwner && header('owner', t('home.columns.owner'))}
          {header('date', dateLabel)}
          <span />
        </div>

        <ul>
          {rows.map(({ node, actions, date, owner, starred, isNew, onToggleStar, to: toOverride, icon, typeLabel: typeOverride }) => {
            const isFolder = node.kind === 'folder';
            const docTemplate = templateFor(node.docType);
            const typeLabel = typeOverride ?? (isFolder
              ? t('folders.folders')
              : docTemplate
                ? t(docTemplate.nameKey)
                : t('folders.files'));
            const to = toOverride ?? (isFolder ? folderPath(node.id) : editorPathFor(node.id, node.docType));

            return (
              <li key={node.id} className="group border-b border-border last:border-b-0">
                <div className={`grid ${columns} items-center gap-3 px-3`}>
                  <Link to={to} className="flex min-w-0 items-center gap-3 py-2">
                    <Icon
                      name={icon ?? (isFolder ? 'folder' : (docTemplate?.icon ?? 'sheets'))}
                      className="h-4 w-4 shrink-0 text-muted"
                    />
                    <span className="min-w-0">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm text-fg">{node.name}</span>
                        {isNew && <NewBadge />}
                      </span>
                      <span className="block truncate text-xs text-muted sm:hidden">
                        {typeLabel} · {date}
                      </span>
                    </span>
                  </Link>
                  <span className="hidden truncate text-sm text-muted sm:block">{typeLabel}</span>
                  {showOwner && (
                    <span className="hidden truncate text-sm text-muted sm:block">{owner ?? ''}</span>
                  )}
                  <span className="hidden truncate text-sm text-muted sm:block">{date}</span>
                  <span className="flex items-center justify-end">
                    {onToggleStar && (
                      <button
                        type="button"
                        aria-label={t(starred ? 'folders.unstar' : 'folders.star')}
                        aria-pressed={starred ?? false}
                        onClick={onToggleStar}
                        className={[
                          'flex h-9 w-9 items-center justify-center rounded-full hover:bg-surface-3',
                          starred
                            ? 'text-accent'
                            : 'text-muted sm:opacity-0 sm:focus-visible:opacity-100 sm:group-hover:opacity-100',
                        ].join(' ')}
                      >
                        <Icon name="star" className="h-4 w-4" filled={starred ?? false} />
                      </button>
                    )}
                    {actions.length > 0 && <NodeMenu nodeName={node.name} actions={actions} />}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
